<script setup>
/**
 * Booking lookup: a record locator or a ticket number, against the provider.
 *
 * Both halves render mapped content now: a ticket shows its passenger, fares, taxes and
 * coupon statuses; a reservation shows its travellers, segments with confirmation status,
 * ticket numbers and provider text.
 *
 * A reservation can arrive without a fare, and that is deliberate rather than a bug -
 * Travelport omits the currency scale from the offer price, and where it cannot be
 * resolved from a same-currency sibling the server declines to guess and returns a
 * warning instead. Warnings render above the detail so a missing fare reads as withheld
 * rather than as zero.
 *
 * The lookup type is explicit rather than sniffed from the input. A six-character
 * locator and a thirteen-digit ticket number are distinguishable, but guessing wrong
 * spends a metered provider call on the wrong endpoint.
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TravelBookingsAPI from 'dashboard/api/travel/bookings';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();

const LOOKUP_TYPES = ['reservation', 'ticket'];
// Mirrors the controllers' formats, so an obvious typo is caught before it costs a
// provider call. The server checks again - this is a courtesy, not the control.
const FORMATS = { reservation: /^[A-Z0-9]{6}$/i, ticket: /^\d{13}$/ };

const lookupType = ref('reservation');
const reference = ref('');
const result = ref(null);
const isLooking = ref(false);

const canLookUp = computed(
  () =>
    FORMATS[lookupType.value].test(reference.value.trim()) && !isLooking.value
);

const selectType = type => {
  lookupType.value = type;
  result.value = null;
};

// Prices arrive as integer minor units plus a decimal place, exactly as Travel::Money
// stores them. Formatting happens here, at the last possible moment.
const formatPrice = price => {
  if (!price) return '—';
  const value = price.amount / 10 ** price.decimal_place;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: price.decimal_place,
  }).format(value);
};

const passengerName = ticket =>
  [ticket.passenger_surname, ticket.passenger_given_name]
    .filter(Boolean)
    .join('/');

// The hosted itinerary. `documentLink` holds what the server returned once - the URL is a
// credential and there is no endpoint that reads it back, so losing it means issuing a new
// one.
const documentLink = ref(null);
const isCreatingDocument = ref(false);

const createDocument = async () => {
  isCreatingDocument.value = true;
  try {
    const { data } = await TravelBookingsAPI.createDocument({
      recordLocator: result.value.reference,
    });
    documentLink.value = data.document.url;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('TRAVEL.BOOKING_LOOKUP.DOCUMENT.ERROR')
    );
  } finally {
    isCreatingDocument.value = false;
  }
};

const copyLink = async () => {
  await navigator.clipboard.writeText(documentLink.value);
  useAlert(t('TRAVEL.BOOKING_LOOKUP.DOCUMENT.COPIED'));
};

const lookUp = async () => {
  isLooking.value = true;
  result.value = null;
  documentLink.value = null;
  const value = reference.value.trim().toUpperCase();
  try {
    if (lookupType.value === 'reservation') {
      const { data } = await TravelBookingsAPI.retrieveReservation(value);
      result.value = {
        kind: 'reservation',
        reference: data.reservation.locator,
        booking: data.reservation,
        warnings: data.warnings || [],
      };
    } else {
      const { data } = await TravelBookingsAPI.retrieveTicket(value);
      result.value = {
        kind: 'ticket',
        reference: data.ticket.number,
        ticket: data.ticket,
      };
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('TRAVEL.BOOKING_LOOKUP.ERROR.GENERIC')
    );
  } finally {
    isLooking.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="w-full max-w-3xl px-6 py-6 mx-auto">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('TRAVEL.BOOKING_LOOKUP.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('TRAVEL.BOOKING_LOOKUP.SUBTITLE') }}
      </p>

      <form
        class="p-4 mt-5 border rounded-xl border-n-weak bg-n-solid-1"
        @submit.prevent="lookUp"
      >
        <div
          class="inline-flex gap-1 p-1 border rounded-lg border-n-weak bg-n-alpha-2"
        >
          <button
            v-for="type in LOOKUP_TYPES"
            :key="type"
            type="button"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="
              lookupType === type
                ? 'bg-n-brand text-white'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="selectType(type)"
          >
            {{ t(`TRAVEL.BOOKING_LOOKUP.TYPES.${type.toUpperCase()}`) }}
          </button>
        </div>

        <div class="flex items-end gap-3 mt-4">
          <Input
            v-model="reference"
            class="flex-1"
            :label="
              t(`TRAVEL.BOOKING_LOOKUP.LABELS.${lookupType.toUpperCase()}`)
            "
            :placeholder="
              t(
                `TRAVEL.BOOKING_LOOKUP.PLACEHOLDERS.${lookupType.toUpperCase()}`
              )
            "
            :maxlength="lookupType === 'reservation' ? 6 : 13"
          />
          <Button
            type="submit"
            :label="t('TRAVEL.BOOKING_LOOKUP.SUBMIT')"
            :is-loading="isLooking"
            :disabled="!canLookUp"
          />
        </div>
      </form>

      <div
        v-if="result"
        class="p-4 mt-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center gap-2">
          <span
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
          >
            {{ t('TRAVEL.BOOKING_LOOKUP.FOUND') }}
          </span>
          <span class="font-mono text-sm text-n-slate-12">
            {{ result.reference }}
          </span>
          <Button
            v-if="result.kind === 'reservation' && !documentLink"
            sm
            faded
            slate
            :is-loading="isCreatingDocument"
            :label="t('TRAVEL.BOOKING_LOOKUP.DOCUMENT.CREATE')"
            @click="createDocument"
          />
        </div>

        <!-- Returned once and never re-readable, so it is shown rather than stored: the
             row keeps only a digest of the token in this URL. -->
        <div
          v-if="documentLink"
          class="flex items-center gap-2 p-3 mt-3 rounded-lg bg-n-alpha-2"
        >
          <p class="flex-1 font-mono text-xs break-all text-n-slate-11">
            {{ documentLink }}
          </p>
          <Button
            sm
            faded
            slate
            :label="t('TRAVEL.BOOKING_LOOKUP.DOCUMENT.COPY')"
            @click="copyLink"
          />
        </div>

        <!-- A retrieve can succeed and still be missing its fare, because we decline to
             guess a currency scale. Saying so beats implying the booking is free. -->
        <p
          v-for="(warning, index) in result.warnings || []"
          :key="index"
          class="p-3 mt-3 text-sm border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-amber-11"
        >
          {{ warning }}
        </p>

        <dl v-if="result.kind === 'reservation'" class="mt-4 space-y-2 text-sm">
          <div v-if="result.booking.travellers.length" class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.BOOKING.TRAVELLERS') }}
            </dt>
            <dd class="text-n-slate-12">
              {{
                result.booking.travellers
                  .map(p => `${p.surname}/${p.given_name}`)
                  .join(', ')
              }}
            </dd>
          </div>
          <div v-if="result.booking.total_price" class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.TICKET.TOTAL') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ formatPrice(result.booking.total_price) }}
            </dd>
          </div>
          <div v-if="result.booking.ticket_numbers.length" class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.BOOKING.TICKETS') }}
            </dt>
            <dd class="flex flex-wrap gap-2">
              <span
                v-for="number in result.booking.ticket_numbers"
                :key="number"
                class="px-2 py-0.5 font-mono text-xs rounded-full bg-n-slate-3 text-n-slate-12"
              >
                {{ number }}
              </span>
            </dd>
          </div>
          <!-- An airline locator is a different reference the agency cannot service
               against, so it is shown as clearly secondary to the PNR above. -->
          <div v-if="result.booking.other_locators.length" class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.BOOKING.OTHER_LOCATORS') }}
            </dt>
            <dd class="flex flex-wrap gap-2">
              <span
                v-for="other in result.booking.other_locators"
                :key="other.value"
                class="px-2 py-0.5 font-mono text-xs rounded-full bg-n-slate-3 text-n-slate-11"
              >
                {{ other.source }} {{ other.value }}
              </span>
            </dd>
          </div>
        </dl>

        <div
          v-if="result.kind === 'reservation' && result.booking.segments.length"
          class="mt-4 overflow-hidden border rounded-lg border-n-weak"
        >
          <div
            v-for="segment in result.booking.segments"
            :key="segment.sequence"
            class="flex items-center justify-between gap-4 px-3 py-2 text-sm border-b last:border-b-0 border-n-weak"
          >
            <span class="font-mono text-n-slate-12">
              {{ segment.carrier }}{{ segment.number }}
            </span>
            <span class="text-n-slate-11">
              {{
                t('TRAVEL.FLIGHT_SEARCH.ROUTE', {
                  from: segment.departure?.location,
                  to: segment.arrival?.location,
                })
              }}
            </span>
            <span class="text-n-slate-11">{{ segment.departure?.date }}</span>
            <span
              class="px-2 py-0.5 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
            >
              {{ segment.status }}
            </span>
          </div>
        </div>

        <div
          v-if="
            result.kind === 'reservation' &&
            (result.booking.remarks.length ||
              result.booking.special_services.length)
          "
          class="p-3 mt-4 rounded-lg bg-n-alpha-2"
        >
          <p class="text-xs uppercase text-n-slate-10">
            {{ t('TRAVEL.BOOKING_LOOKUP.BOOKING.PROVIDER_TEXT') }}
          </p>
          <p
            v-for="(remark, index) in result.booking.remarks"
            :key="`remark-${index}`"
            class="mt-1 font-mono text-xs break-all text-n-slate-11"
          >
            {{ remark }}
          </p>
          <p
            v-for="service in result.booking.special_services"
            :key="service.code"
            class="mt-1 font-mono text-xs break-all text-n-slate-11"
          >
            {{ service.code }} {{ service.text }}
          </p>
        </div>

        <dl v-if="result.kind === 'ticket'" class="mt-4 space-y-2 text-sm">
          <div class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.TICKET.PASSENGER') }}
            </dt>
            <dd class="text-n-slate-12">{{ passengerName(result.ticket) }}</dd>
          </div>
          <div class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.TICKET.CARRIER') }}
            </dt>
            <dd class="font-mono text-n-slate-12">
              {{ result.ticket.validating_carrier_code }}
            </dd>
          </div>
          <div class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.TICKET.BASE') }}
            </dt>
            <dd class="text-n-slate-12">
              {{ formatPrice(result.ticket.base_price) }}
            </dd>
          </div>
          <div class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.TICKET.TOTAL') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ formatPrice(result.ticket.total_price) }}
            </dd>
          </div>
          <div v-if="result.ticket.taxes.length" class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.TICKET.TAXES') }}
            </dt>
            <dd class="flex flex-wrap gap-2">
              <span
                v-for="tax in result.ticket.taxes"
                :key="tax.code"
                class="px-2 py-0.5 text-xs rounded-full bg-n-slate-3 text-n-slate-12"
              >
                {{ tax.code }} {{ formatPrice(tax.amount) }}
              </span>
            </dd>
          </div>
          <div v-if="result.ticket.coupon_statuses.length" class="flex gap-3">
            <dt class="w-40 shrink-0 text-n-slate-11">
              {{ t('TRAVEL.BOOKING_LOOKUP.TICKET.COUPONS') }}
            </dt>
            <dd class="flex flex-wrap gap-2">
              <span
                v-for="(status, index) in result.ticket.coupon_statuses"
                :key="index"
                class="px-2 py-0.5 text-xs rounded-full bg-n-slate-3 text-n-slate-12"
              >
                {{ status }}
              </span>
            </dd>
          </div>
        </dl>

        <!-- Carrier free text, sanitized server-side through UntrustedText (T7). Kept
             visually apart from the mapped fields above so it reads as quoted provider
             output rather than as something this product asserts. -->
        <div
          v-if="
            result.kind === 'ticket' &&
            (result.ticket.fare_breakdown || result.ticket.restrictions.length)
          "
          class="p-3 mt-4 rounded-lg bg-n-alpha-2"
        >
          <p class="text-xs uppercase text-n-slate-10">
            {{ t('TRAVEL.BOOKING_LOOKUP.TICKET.CARRIER_TEXT') }}
          </p>
          <p
            v-if="result.ticket.fare_breakdown"
            class="mt-1 font-mono text-xs break-all text-n-slate-11"
          >
            {{ result.ticket.fare_breakdown }}
          </p>
          <p
            v-for="(restriction, index) in result.ticket.restrictions"
            :key="index"
            class="mt-1 font-mono text-xs break-all text-n-slate-11"
          >
            {{ restriction }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
